import { useEffect, useState } from 'react';
import { useConvex, useQuery, Authenticated, Unauthenticated, AuthLoading } from 'convex/react';
import { api } from '../convex/_generated/api';
import type { Id } from '../convex/_generated/dataModel';
import { Header } from './components/Header';
import { FileUploader } from './components/FileUploader';
import { MeetingProgress } from './components/MeetingProgress';
import { TranscriptionPanel } from './components/TranscriptionPanel';
import { SummaryPanel } from './components/SummaryPanel';
import { HistoryList } from './components/HistoryList';
import { MeetingDetail } from './components/MeetingDetail';
import { MatrixRain } from './components/MatrixRain';
import { Login } from './components/Login';
import { uploadAndCreateMeeting, type UploadProgress } from './lib/upload';
import { DEFAULT_SUMMARY_MODEL, DEFAULT_TRANSCRIPTION_MODEL } from './lib/models';
import type { ClientStage, FileInfo } from './types';

type View =
  | { kind: 'home' }
  | { kind: 'history' }
  | { kind: 'detail'; meetingId: Id<'meetings'> };

export type VisualTheme = 'matrix' | 'neo';

const THEME_STORAGE_KEY = 'meeting-transcriber-theme';

function getInitialTheme(): VisualTheme {
  if (typeof window === 'undefined') return 'matrix';
  return window.localStorage.getItem(THEME_STORAGE_KEY) === 'neo' ? 'neo' : 'matrix';
}

function App() {
  const convex = useConvex();
  const [view, setView] = useState<View>({ kind: 'home' });
  const [visualTheme, setVisualTheme] = useState<VisualTheme>(getInitialTheme);

  // Estado del flujo de subida (cliente)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [clientStage, setClientStage] = useState<ClientStage>('idle');
  const [clientProgress, setClientProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string>('');
  const [activeMeetingId, setActiveMeetingId] = useState<Id<'meetings'> | null>(null);
  const [transcriptionModel, setTranscriptionModel] = useState<string>(DEFAULT_TRANSCRIPTION_MODEL);
  const [summaryModel, setSummaryModel] = useState<string>(DEFAULT_SUMMARY_MODEL);
  const [summaryContext, setSummaryContext] = useState<string>('');

  // Suscripción reactiva al meeting que acabamos de crear (si lo hay)
  const activeMeetingData = useQuery(
    api.meetings.get,
    activeMeetingId ? { meetingId: activeMeetingId } : 'skip'
  );

  const handleProcess = async () => {
    if (!fileInfo) return;
    setError('');
    setActiveMeetingId(null);

    try {
      const meetingId = await uploadAndCreateMeeting(
        convex,
        fileInfo.file,
        fileInfo.durationSec,
        (p) => {
          setClientProgress(p);
          setClientStage(p.stage);
        },
        { transcriptionModel, summaryModel, summaryContext }
      );
      setActiveMeetingId(meetingId);
      setClientStage('submitted');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setClientStage('error');
    }
  };

  const handleResetUpload = () => {
    setFileInfo(null);
    setClientStage('idle');
    setClientProgress(null);
    setActiveMeetingId(null);
    setSummaryContext('');
    setError('');
  };

  const isProcessing =
    clientStage === 'chunking' ||
    clientStage === 'uploading' ||
    clientStage === 'creating-meeting' ||
    (clientStage === 'submitted' &&
      activeMeetingData != null &&
      activeMeetingData.meeting.status !== 'done' &&
      activeMeetingData.meeting.status !== 'error');

  const meetingDoc = activeMeetingData?.meeting ?? null;
  const chunks = activeMeetingData?.chunks ?? [];

  useEffect(() => {
    document.documentElement.dataset.theme = visualTheme;
    window.localStorage.setItem(THEME_STORAGE_KEY, visualTheme);
  }, [visualTheme]);

  return (
    <div className="min-h-dvh">
      <MatrixRain />

      <AuthLoading>
        <div className="min-h-dvh flex items-center justify-center p-6 font-mono text-xs text-[var(--color-asphalt)]">
          <div className="flex items-center gap-3">
            <span className="spinner-tech" />
            <span>ESTABLECIENDO CONEXIÓN SEGURA...</span>
          </div>
        </div>
      </AuthLoading>

      <Unauthenticated>
        <Login />
      </Unauthenticated>

      <Authenticated>
        <main className="max-w-4xl mx-auto px-6 sm:px-10 py-12 sm:py-20 space-y-12 sm:space-y-16 above-rain">
          <Header
            currentView={view.kind === 'detail' ? 'history' : view.kind}
            onNavigate={(v) => setView({ kind: v })}
            visualTheme={visualTheme}
            onToggleTheme={() => setVisualTheme((theme) => (theme === 'matrix' ? 'neo' : 'matrix'))}
          />

          {view.kind === 'home' && (
            <>
              <FileUploader
                onFileSelected={setFileInfo}
                onProcess={handleProcess}
                current={fileInfo}
                disabled={isProcessing}
                isProcessing={isProcessing}
                transcriptionModel={transcriptionModel}
                summaryModel={summaryModel}
                summaryContext={summaryContext}
                onTranscriptionModelChange={setTranscriptionModel}
                onSummaryModelChange={setSummaryModel}
                onSummaryContextChange={setSummaryContext}
              />

              <MeetingProgress
                clientStage={clientStage}
                clientMessage={clientProgress?.message}
                clientFraction={clientProgress?.fraction}
                clientChunkIndex={clientProgress?.chunkIndex}
                clientTotalChunks={clientProgress?.totalChunks}
                meeting={meetingDoc}
                chunks={chunks}
                selectedTranscriptionModel={transcriptionModel}
                errorMessage={error}
              />

              {/* Resultados en línea cuando termina el meeting */}
              {meetingDoc?.transcript && (
                <TranscriptionPanel
                  transcript={meetingDoc.transcript}
                  audioFileName={meetingDoc.fileName}
                />
              )}

              {meetingDoc?.summary && meetingDoc.status === 'done' && (
                <SummaryPanel summary={meetingDoc.summary} audioFileName={meetingDoc.fileName} />
              )}

              {(clientStage === 'error' || meetingDoc?.status === 'done' || meetingDoc?.status === 'error') && (
                <div className="flex justify-center pt-4 no-print">
                  <button
                    type="button"
                    onClick={handleResetUpload}
                    className="term-link px-5 py-2.5"
                  >
                    ← exec --reset
                  </button>
                </div>
              )}
            </>
          )}

          {view.kind === 'history' && (
            <HistoryList onSelect={(id) => setView({ kind: 'detail', meetingId: id })} />
          )}

          {view.kind === 'detail' && (
            <MeetingDetail
              meetingId={view.meetingId}
              onBack={() => setView({ kind: 'history' })}
            />
          )}

          <footer className="pt-12 border-t border-[var(--color-asphalt-300)] font-mono text-[10px] text-[var(--color-asphalt-500)] uppercase tracking-[0.18em] text-center">
            <span className="text-[var(--color-amber-mid)]">[stack]</span> openrouter-stt · convex · vite_8 · react_19
          </footer>
        </main>
      </Authenticated>
    </div>
  );
}

export default App;
